import { useCallback, useEffect, useMemo, useState } from 'react';
import { IAttachRow } from './AttachGrid.types';

export interface IUseAttachSelection {
    selected: Set<string>;
    isSelected: (key: string) => boolean;
    toggle: (key: string) => void;
    toggleAll: () => void;
    /** Выбрать весь отфильтрованный набор — все страницы, не только показанную. */
    selectAll: () => void;
    clear: () => void;
    /** Добавить и снять ключи, не трогая остальной выбор (выбор на одной странице таблицы). */
    apply: (added: string[], removed: string[]) => void;
    allSelected: boolean;
    someSelected: boolean;
    count: number;
}

/** Выбор строк. При recursive выбор файла переключает и его загруженные версии. */
export function useAttachSelection(
    rows: IAttachRow[],
    childrenByParent: Record<string, IAttachRow[]>,
    recursive: boolean,
): IUseAttachSelection {
    const [selected, setSelected] = useState<Set<string>>(new Set());

    const toggle = useCallback(
        (key: string) => {
            setSelected((prev) => {
                const willSelect = !prev.has(key);
                const next = new Set(prev);
                if (willSelect) next.add(key);
                else next.delete(key);
                if (recursive) {
                    const children = childrenByParent[key];
                    if (children) {
                        for (const c of children) {
                            if (willSelect) next.add(c.key);
                            else next.delete(c.key);
                        }
                    }
                }
                return next;
            });
        },
        [recursive, childrenByParent],
    );

    const allKeys = useMemo(() => rows.map((r) => r.key), [rows]);
    const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
    const someSelected = allKeys.some((k) => selected.has(k));

    const toggleAll = useCallback(() => {
        setSelected((prev) => {
            const everySelected = allKeys.length > 0 && allKeys.every((k) => prev.has(k));
            return everySelected ? new Set<string>() : new Set(allKeys);
        });
    }, [allKeys]);

    const selectAll = useCallback(() => {
        const keys = recursive
            ? [...allKeys, ...allKeys.flatMap((k) => childrenByParent[k]?.map((c) => c.key) ?? [])]
            : allKeys;
        setSelected(new Set(keys));
    }, [allKeys, recursive, childrenByParent]);

    // Удалённые и отфильтрованные файлы выпадают из выбора: иначе счётчик панели расходится
    // с тем, над чем выполнятся действия.
    useEffect(() => {
        const known = new Set(allKeys);
        Object.values(childrenByParent).forEach((children) => children.forEach((c) => known.add(c.key)));
        setSelected((prev) => {
            const kept = [...prev].filter((k) => known.has(k));
            return kept.length === prev.size ? prev : new Set(kept);
        });
    }, [allKeys, childrenByParent]);

    const clear = useCallback(() => setSelected(new Set()), []);
    const isSelected = useCallback((key: string) => selected.has(key), [selected]);

    const apply = useCallback((added: string[], removed: string[]) => {
        if (!added.length && !removed.length) return;
        setSelected((prev) => {
            const next = new Set(prev);
            added.forEach((k) => next.add(k));
            removed.forEach((k) => next.delete(k));
            return next.size === prev.size && [...next].every((k) => prev.has(k)) ? prev : next;
        });
    }, []);

    return {
        selected,
        isSelected,
        toggle,
        toggleAll,
        selectAll,
        clear,
        apply,
        allSelected,
        someSelected,
        count: selected.size,
    };
}
